import { sanitizeRteForSsr, SsrTagClassMap } from "./rteSsrSanitizer";

export const isSSREnvironment = (): boolean =>
  typeof window === "undefined" || !!(window as any).__isSSR;

export type SanitizeConfig = {
  options?: any;
  ssrTagClassMap?: SsrTagClassMap;
};

export const sanitizeOrPassthrough = (
  purifier: { sanitize: (input: string, options?: any) => string },
  html: string | undefined | null,
  config?: SanitizeConfig
): string => {
  if (!html) {
    return "";
  }

  if (isSSREnvironment()) {
    return sanitizeRteForSsr(html, config?.ssrTagClassMap, config?.options);
  }

  return purifier.sanitize(html, config?.options);
};

export const cleanCSSPropertyInRecurence = (
  node: HTMLElement,
  cssProperty: string
) => {
  if (node.style?.[cssProperty]) {
    node.style.removeProperty(cssProperty);
  }
  node.childNodes.forEach((child: HTMLElement) => {
    cleanCSSPropertyInRecurence(child, cssProperty);
  });
};

export const checkDataAttributeExistenceInRecurrence = (
  node: HTMLElement,
  dataAttribute: string
) => {
  if (!node?.dataset) {
    return false;
  }

  if (node.dataset[dataAttribute]) {
    return true;
  }
  return node.childNodes
    ? Array.from(node.childNodes).some((child: HTMLElement) =>
        checkDataAttributeExistenceInRecurrence(child, dataAttribute)
      )
    : false;
};

export const checkChildrenExitenceInRecurrence = (
  node: HTMLElement,
  nodeName: string
) => {
  if (
    node.childNodes.length > 0 &&
    Array.from(node.childNodes).some(
      (child: HTMLElement) => child.nodeName === nodeName
    )
  ) {
    return true;
  }
  return node.childNodes
    ? Array.from(node.childNodes).some((child: HTMLElement) =>
        checkChildrenExitenceInRecurrence(child, nodeName)
      )
    : false;
};

export const checkClassExistenceInRecurence = (
  node: HTMLElement,
  className: string
) => {
  if (node.classList?.contains(className)) {
    return true;
  }
  return node.childNodes
    ? Array.from(node.childNodes).some((child: HTMLElement) =>
        checkClassExistenceInRecurence(child, className)
      )
    : false;
};

export const preprocessHTMLForIframes = (htmlString: string): string => {
  if (isSSREnvironment()) {
    return htmlString;
  }

  const doc = new DOMParser().parseFromString(htmlString, "text/html");

  const divsWithIframes = Array.from(doc.querySelectorAll("div")).filter(
    (div) =>
      div.children.length > 0 &&
      div.children[0].tagName === "IFRAME" &&
      !div.parentElement?.hasAttribute("data-iframe-wrapped")
  );

  divsWithIframes.forEach((div) => {
    const wrapper = doc.createElement("div");
    wrapper.className = "max-w-[1192px] w-full mb-8 mx-auto";
    wrapper.setAttribute("data-iframe-wrapped", "true");

    div.parentNode?.insertBefore(wrapper, div);

    wrapper.appendChild(div);
  });

  return doc.body.innerHTML;
};
